<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import IconBxsArrowFromLeft from '~icons/bxs/arrow-from-left';
import IconBxsArrowFromRight from '~icons/bxs/arrow-from-right';
import IconBxsFilterAlt from '~icons/bxs/filter-alt';
import IconBxsMessageAltCheck from '~icons/bxs/message-alt-check';
import IconLucideMessageSquareMore from '~icons/lucide/message-square-more';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import CButton from './primitives/CButton.vue';
import CIconButton from './primitives/CIconButton.vue';
import CInput from './primitives/CInput.vue';
import CMenu from './primitives/CMenu.vue';
import CMenuItem from './primitives/CMenuItem.vue';

type ChatMessage = {
  body: string;
  fromDisplayName: string;
  fromParticipantId: Id<'participants'>;
  id: string;
  isFunneled: boolean;
  isOwn: boolean;
  sentAt: number;
  threadKey: string;
  toDisplayName: string | null;
  toParticipantId: Id<'participants'> | null;
};

type ChatState = {
  conference: {
    chatEnabled: boolean;
  };
  currentRoom: {
    displayName: string;
    funnelChat: boolean;
    slug: string;
    threadKey: string;
  };
  directMessages: ChatMessage[];
  participants: Array<{
    displayName: string;
    id: Id<'participants'>;
    isPresent: boolean;
    role: 'student' | 'teacher' | 'recorder';
  }>;
  roomMessages: ChatMessage[];
  viewer: {
    canDirectChat: boolean;
    canManageChat: boolean;
    displayName: string;
    id: Id<'participants'>;
    isKicked: boolean;
    role: 'student' | 'teacher' | 'recorder';
  };
};

type ChatActivityState = {
  typing: Array<{
    displayName: string;
    participantId: Id<'participants'>;
    targetKey: string;
    updatedAt: number;
  }>;
};

const props = withDefaults(
  defineProps<{
    chatState?: ChatState | null;
    conferenceSlug: string;
    dockable?: boolean;
    docked?: boolean;
    currentRoomSlug?: string;
    requestedThreadKey?: string | null;
    rooms?: Array<{ displayName: string; slug: string }>;
  }>(),
  {
    dockable: false,
    docked: false,
    requestedThreadKey: null,
  },
);

const emit = defineEmits<{
  (event: 'dock-right'): void;
  (event: 'dock-left'): void;
}>();

type ThreadTarget =
  | {
      key: string;
      kind: 'room';
      label: string;
      roomSlug: string;
    }
  | {
      key: string;
      kind: 'direct';
      label: string;
      participantId: Id<'participants'>;
    };

const typingTimeoutMs = 1_500;
const typingActivityWindowMs = 5_000;
const activityClockIntervalMs = 500;
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

function buildDirectThreadKey(leftParticipantId: string, rightParticipantId: string): string {
  return `direct:${[leftParticipantId, rightParticipantId].sort().join(':')}`;
}

const queryArgs = computed(() => ({
  conferenceSlug: props.conferenceSlug,
}));

const fallbackChatStateQuery = props.chatState === undefined ? useConvexQuery(api.chat.getState, queryArgs) : null;
const chatState = computed<ChatState | null>(() => {
  if (props.chatState !== undefined) {
    return props.chatState;
  }

  return (fallbackChatStateQuery?.data.value as ChatState | null | undefined) ?? null;
});
const chatStateError = computed(() => fallbackChatStateQuery?.error.value ?? null);
const isChatStatePending = computed(() => fallbackChatStateQuery?.isPending.value ?? false);
const { data: chatActivityStateData } = useConvexQuery(api.chat.getActivity, queryArgs);
const chatActivityState = computed(() => (chatActivityStateData.value as ChatActivityState | null | undefined) ?? null);
const { mutate: sendRoomMessage, isPending: isRoomMessagePending } = useConvexMutation(api.chat.sendRoomMessage);
const { mutate: sendDirectMessage, isPending: isDirectMessagePending } = useConvexMutation(api.chat.sendDirectMessage);
const { mutate: setChatEnabled, isPending: isChatEnabledPending } = useConvexMutation(api.chat.setChatEnabled);
const { mutate: setFunnelChat, isPending: isFunnelChatPending } = useConvexMutation(api.chat.setFunnelChat);
const { mutate: setTypingState } = useConvexMutation(api.presence.setTypingState);

const chatError = ref<string | null>(null);
const messageDraft = ref<string | null>('');
const selectedThreadKey = ref('');
const showThreadPicker = ref(false);
const messageListRef = ref<HTMLElement | null>(null);
const activityNow = ref(Date.now());
const threadIndicators = ref<Record<string, boolean>>({});
let typingStopHandle: number | null = null;
let activityClockHandle: number | null = null;
let activeTypingKey: string | null = null;
let hasInitializedMessageIndicators = false;
let seenMessageIds = new Set<string>();

function setThreadIndicator(threadKey: string, value: boolean): void {
  if (!threadKey) {
    return;
  }

  if (value) {
    threadIndicators.value = {
      ...threadIndicators.value,
      [threadKey]: true,
    };
    return;
  }

  if (!(threadKey in threadIndicators.value)) {
    return;
  }

  const nextIndicators = { ...threadIndicators.value };
  delete nextIndicators[threadKey];
  threadIndicators.value = nextIndicators;
}

const availableThreads = computed<ThreadTarget[]>(() => {
  const state = chatState.value;

  if (!state) {
    return [];
  }

  const roomThread: ThreadTarget = {
    key: state.currentRoom.threadKey,
    kind: 'room',
    label: state.currentRoom.displayName,
    roomSlug: state.currentRoom.slug,
  };

  const visibleDirectThreadKeys = new Set(state.directMessages.map((message) => message.threadKey));

  if (selectedThreadKey.value.startsWith('direct:')) {
    visibleDirectThreadKeys.add(selectedThreadKey.value);
  }

  const directThreads = state.participants
    .map((participant: (typeof state.participants)[number]) => ({
      key: buildDirectThreadKey(state.viewer.id, participant.id),
      kind: 'direct' as const,
      label: participant.displayName,
      participantId: participant.id,
    }))
    .filter((thread) => visibleDirectThreadKeys.has(thread.key));

  return [roomThread, ...directThreads];
});

const selectedThread = computed<ThreadTarget | null>(() => {
  return availableThreads.value.find((thread) => thread.key === selectedThreadKey.value) ?? availableThreads.value[0] ?? null;
});

const selectedMessages = computed(() => {
  if (!chatState.value || !selectedThread.value) {
    return [];
  }

  if (selectedThread.value.kind === 'room') {
    return [...chatState.value.roomMessages, ...chatState.value.directMessages].sort((left, right) => left.sentAt - right.sentAt);
  }

  return [...chatState.value.directMessages]
    .filter((message) => message.threadKey === selectedThread.value?.key)
    .sort((left, right) => left.sentAt - right.sentAt);
});

const typingParticipants = computed(() => {
  const activityState = chatActivityState.value;

  if (!activityState || !selectedThread.value) {
    return [];
  }

  return activityState.typing
    .filter(
      (state: (typeof activityState.typing)[number]) =>
        state.targetKey === selectedThread.value?.key && activityNow.value - state.updatedAt <= typingActivityWindowMs,
    )
    .map((state: (typeof activityState.typing)[number]) => state.displayName);
});

function startActivityClock(): void {
  if (activityClockHandle !== null) {
    return;
  }

  activityClockHandle = window.setInterval(() => {
    activityNow.value = Date.now();
  }, activityClockIntervalMs);
}

function stopActivityClock(): void {
  if (activityClockHandle === null) {
    return;
  }

  window.clearInterval(activityClockHandle);
  activityClockHandle = null;
}

const typingLabel = computed(() => {
  if (typingParticipants.value.length === 0) {
    return '';
  }

  if (typingParticipants.value.length === 1) {
    return `${typingParticipants.value[0]} is typing...`;
  }

  if (typingParticipants.value.length === 2) {
    return `${typingParticipants.value[0]} and ${typingParticipants.value[1]} are typing...`;
  }

  return `${typingParticipants.value[0]} and others are typing...`;
});

const isComposerDisabled = computed(() => {
  if (!selectedThread.value || !chatState.value || chatState.value.viewer.isKicked) {
    return true;
  }

  if (selectedThread.value.kind === 'direct') {
    return !chatState.value.viewer.canDirectChat;
  }

  return false;
});

const isSendPending = computed(() => isRoomMessagePending.value || isDirectMessagePending.value);
const hasUnreadThread = computed(() =>
  Object.entries(threadIndicators.value).some(([threadKey, hasUnread]) => threadKey !== selectedThreadKey.value && hasUnread),
);
const canToggleChatEnabled = computed(() => chatState.value?.viewer.canManageChat && !isChatEnabledPending.value);
const canToggleFunnelChat = computed(() => chatState.value?.viewer.role === 'teacher' && !isFunnelChatPending.value);
const chatEnabledToggleTitle = computed(() => (chatState.value?.conference.chatEnabled ? 'Disable Chat' : 'Enable Chat'));
const funnelToggleTitle = computed(() =>
  chatState.value?.currentRoom.funnelChat ? 'Disable Room Chat Funneling' : 'Enable Room Chat Funneling',
);
const rootClass = computed(() => 'flex min-h-0 flex-1 flex-col bg-conference-dock');
const messageListClass = computed(() => 'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-conference-dock-muted px-2 py-2');
const headingLabel = computed(() => {
  if (selectedThread.value?.kind === 'direct') {
    return `Chat - ${selectedThread.value.label}`;
  }

  return `Chat - ${chatState.value?.currentRoom.displayName ?? 'Main Room'}`;
});
const menuTriggerTone = computed(() => (showThreadPicker.value || hasUnreadThread.value ? 'gold' : 'neutral'));
const chatEnabledToggleButtonClass = computed(() =>
  chatState.value?.conference.chatEnabled
    ? ''
    : 'border-red-500 text-red-500 enabled:hover:border-red-400 enabled:hover:text-red-400',
);
const funnelToggleButtonClass = computed(() =>
  chatState.value?.currentRoom.funnelChat
    ? 'border-red-500 text-red-500 enabled:hover:border-red-400 enabled:hover:text-red-400'
    : '',
);

async function pushTypingState(value: boolean): Promise<void> {
  if (!selectedThread.value || !chatState.value || chatState.value.viewer.isKicked) {
    return;
  }

  try {
    if (selectedThread.value.kind === 'room') {
      await setTypingState({
        conferenceSlug: props.conferenceSlug,
        target: {
          kind: 'room',
          roomSlug: selectedThread.value.roomSlug,
        },
        value,
      });
    } else {
      await setTypingState({
        conferenceSlug: props.conferenceSlug,
        target: {
          kind: 'direct',
          participantId: selectedThread.value.participantId,
        },
        value,
      });
    }

    activeTypingKey = value ? selectedThread.value.key : null;
  } catch (error) {
    chatError.value = error instanceof Error ? error.message : 'Unable to update typing state.';
  }
}

function resetTypingTimer(): void {
  if (typingStopHandle !== null) {
    window.clearTimeout(typingStopHandle);
  }

  typingStopHandle = window.setTimeout(() => {
    void pushTypingState(false);
    typingStopHandle = null;
  }, typingTimeoutMs);
}

function handleComposerInput(): void {
  if (!messageDraft.value?.trim()) {
    if (typingStopHandle !== null) {
      window.clearTimeout(typingStopHandle);
      typingStopHandle = null;
    }
    void pushTypingState(false);
    return;
  }

  if (activeTypingKey !== selectedThread.value?.key) {
    void pushTypingState(true);
  }

  resetTypingTimer();
}

async function handleSendMessage(): Promise<void> {
  const body = messageDraft.value?.trim() ?? '';

  if (!body || !selectedThread.value) {
    return;
  }

  try {
    chatError.value = null;

    if (selectedThread.value.kind === 'room') {
      await sendRoomMessage({
        body,
        conferenceSlug: props.conferenceSlug,
      });
    } else {
      await sendDirectMessage({
        body,
        conferenceSlug: props.conferenceSlug,
        participantId: selectedThread.value.participantId,
      });
    }

    messageDraft.value = '';
    if (typingStopHandle !== null) {
      window.clearTimeout(typingStopHandle);
      typingStopHandle = null;
    }
    await pushTypingState(false);
    await nextTick();
    messageListRef.value?.scrollTo({ behavior: 'smooth', top: messageListRef.value.scrollHeight });
  } catch (error) {
    chatError.value = error instanceof Error ? error.message : 'Unable to send the chat message.';
  }
}

async function toggleChatEnabled(): Promise<void> {
  if (!chatState.value?.viewer.canManageChat) {
    return;
  }

  try {
    chatError.value = null;
    await setChatEnabled({
      conferenceSlug: props.conferenceSlug,
      value: !chatState.value.conference.chatEnabled,
    });
  } catch (error) {
    chatError.value = error instanceof Error ? error.message : 'Unable to update chat availability.';
  }
}

async function toggleFunnelChat(): Promise<void> {
  if (!chatState.value || chatState.value.viewer.role !== 'teacher') {
    return;
  }

  try {
    chatError.value = null;
    await setFunnelChat({
      conferenceSlug: props.conferenceSlug,
      value: !chatState.value.currentRoom.funnelChat,
    });
  } catch (error) {
    chatError.value = error instanceof Error ? error.message : 'Unable to update chat funneling.';
  }
}

function selectThreadFromMenu(threadKey: string, closeMenu: () => void): void {
  selectedThreadKey.value = threadKey;
  closeMenu();
}

watch(
  availableThreads,
  (threads) => {
    if (threads.length === 0) {
      selectedThreadKey.value = '';
      return;
    }

    if (!threads.some((thread) => thread.key === selectedThreadKey.value)) {
      selectedThreadKey.value = threads[0].key;
    }
  },
  { immediate: true },
);

watch(
  () => props.requestedThreadKey,
  (requestedThreadKey) => {
    if (!requestedThreadKey || selectedThreadKey.value === requestedThreadKey) {
      return;
    }

    selectedThreadKey.value = requestedThreadKey;
  },
  { immediate: true },
);

watch(selectedThreadKey, () => {
  if (typingStopHandle !== null) {
    window.clearTimeout(typingStopHandle);
    typingStopHandle = null;
  }
  setThreadIndicator(selectedThreadKey.value, false);
  void pushTypingState(false);
  showThreadPicker.value = false;
});

watch(
  () => selectedMessages.value.length,
  async () => {
    await nextTick();
    messageListRef.value?.scrollTo({ behavior: 'smooth', top: messageListRef.value.scrollHeight });
  },
);

watch(
  () => ({ directMessages: chatState.value?.directMessages ?? [], roomMessages: chatState.value?.roomMessages ?? [] }),
  ({ directMessages, roomMessages }) => {
    const nextSeenMessageIds = new Set<string>();

    for (const message of roomMessages) {
      const messageId = String(message.id);
      nextSeenMessageIds.add(messageId);

      if (hasInitializedMessageIndicators && !seenMessageIds.has(messageId) && selectedThreadKey.value !== message.threadKey) {
        setThreadIndicator(message.threadKey, true);
      }
    }

    for (const message of directMessages) {
      const messageId = String(message.id);
      nextSeenMessageIds.add(messageId);

      if (hasInitializedMessageIndicators && !seenMessageIds.has(messageId) && selectedThreadKey.value !== message.threadKey) {
        setThreadIndicator(message.threadKey, true);
      }
    }

    seenMessageIds = nextSeenMessageIds;
    hasInitializedMessageIndicators = true;
  },
  { immediate: true },
);

if (typeof window !== 'undefined') {
  startActivityClock();
}

onBeforeUnmount(() => {
  if (typingStopHandle !== null) {
    window.clearTimeout(typingStopHandle);
  }
  stopActivityClock();
  void pushTypingState(false);
});
</script>

<template>
  <section :class="rootClass" data-testid="chat-panel">
    <div
      class="border-conference-line-muted bg-conference-structure flex min-w-0 items-center justify-between gap-2 border-b px-1.5 py-1 text-white"
    >
      <div class="flex min-w-0 items-center gap-1.5">
        <CMenu
          v-if="chatState"
          v-model:open="showThreadPicker"
          align="left"
          panel-test-id="chat-thread-menu"
          tone="chrome"
          width="panel"
        >
          <template #trigger="{ open, toggle }">
            <CIconButton
              size="sm"
              :tone="open ? 'gold' : menuTriggerTone"
              aria-haspopup="menu"
              :aria-expanded="open"
              :aria-label="open ? 'Hide chats' : 'Show chats'"
              :title="open ? 'Hide chats' : 'Show chats'"
              @click="toggle"
            >
              <IconLucideMessageSquareMore class="size-3.5" />
            </CIconButton>
          </template>

          <template #default="{ close }">
            <div class="conference-text-control text-white" style="width: 186px">
              <div class="py-1">
                <CMenuItem v-if="availableThreads[0]" @click="selectThreadFromMenu(availableThreads[0].key, close)">
                  <template #leading>
                    <IconBxsMessageAltCheck
                      class="size-3 shrink-0"
                      :class="selectedThreadKey === availableThreads[0].key ? 'opacity-100' : 'opacity-0'"
                    />
                  </template>
                  {{ availableThreads[0].label }}
                  <template #trailing>
                    <span
                      v-if="threadIndicators[availableThreads[0].key]"
                      aria-hidden="true"
                      class="bg-conference-accent size-1.5 rounded-full"
                    />
                  </template>
                </CMenuItem>

                <CMenuItem
                  v-for="thread in availableThreads.slice(1)"
                  :key="thread.key"
                  @click="selectThreadFromMenu(thread.key, close)"
                >
                  <template #leading>
                    <IconBxsMessageAltCheck
                      class="size-3 shrink-0"
                      :class="selectedThreadKey === thread.key ? 'opacity-100' : 'opacity-0'"
                    />
                  </template>
                  {{ thread.label }}
                  <template #trailing>
                    <span
                      v-if="threadIndicators[thread.key]"
                      aria-hidden="true"
                      class="bg-conference-accent size-1.5 rounded-full"
                    />
                  </template>
                </CMenuItem>
              </div>
            </div>
          </template>
        </CMenu>

        <p data-testid="chat-heading" class="truncate text-[11px] tracking-[0.14em] uppercase">
          {{ headingLabel }}
        </p>
      </div>

      <div class="ml-auto flex shrink-0 items-center gap-1">
        <CIconButton
          v-if="chatState?.viewer.canManageChat"
          size="sm"
          tone="neutral"
          :class="chatEnabledToggleButtonClass"
          :disabled="!canToggleChatEnabled"
          :aria-label="canToggleChatEnabled ? chatEnabledToggleTitle : 'Chat availability is only available to teachers'"
          :title="canToggleChatEnabled ? chatEnabledToggleTitle : 'Chat availability is only available to teachers'"
          @click="toggleChatEnabled"
        >
          <IconBxsMessageAltCheck class="size-3.5" />
        </CIconButton>

        <CIconButton
          size="sm"
          tone="neutral"
          :class="funnelToggleButtonClass"
          :disabled="!canToggleFunnelChat"
          :aria-label="canToggleFunnelChat ? funnelToggleTitle : 'Chat funneling is only available to teachers'"
          :title="canToggleFunnelChat ? funnelToggleTitle : 'Chat funneling is only available to teachers'"
          @click="toggleFunnelChat"
        >
          <IconBxsFilterAlt class="size-3.5" />
        </CIconButton>

        <CIconButton
          v-if="!props.docked && props.dockable"
          size="sm"
          tone="neutral"
          aria-label="Dock right"
          title="Dock right"
          @click="emit('dock-right')"
        >
          <IconBxsArrowFromLeft class="size-3.5" />
        </CIconButton>

        <CIconButton
          v-else-if="props.docked && props.dockable"
          size="sm"
          tone="neutral"
          aria-label="Dock Left"
          title="Dock Left"
          @click="emit('dock-left')"
        >
          <IconBxsArrowFromRight class="size-3.5" />
        </CIconButton>
      </div>
    </div>

    <div
      v-if="chatError || chatStateError"
      class="conference-text-label m-2 rounded-[3px] border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700"
    >
      {{ chatError ?? chatStateError?.message }}
    </div>

    <div
      v-if="isChatStatePending && !chatState"
      :class="
        props.docked
          ? 'border-conference-line-soft conference-text-label m-2 rounded-[3px] border bg-white px-3 py-3 text-slate-700'
          : 'border-conference-dock-border bg-conference-dock-subtle conference-text-label m-2 rounded-[3px] border px-3 py-3 text-slate-700'
      "
    >
      Syncing chat threads from Convex...
    </div>

    <template v-else-if="chatState">
      <div
        v-if="!chatState.conference.chatEnabled && chatState.viewer.canManageChat"
        class="conference-text-label m-2 rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900"
        data-testid="chat-disabled-banner"
      >
        Chat is disabled for students. Re-enable it here when the room should be able to chat again.
      </div>

      <div
        v-if="!chatState.viewer.canDirectChat && chatState.viewer.role !== 'teacher'"
        class="conference-text-label m-2 rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900"
        data-testid="chat-permission-banner"
      >
        Your direct chat access is off, so room messages stay visible to teachers only.
      </div>

      <div
        v-if="chatState.currentRoom.funnelChat && chatState.viewer.role !== 'teacher'"
        class="conference-text-label m-2 rounded-[3px] border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800"
      >
        Student and recorder messages in this room are currently funneled to teachers first.
      </div>

      <div ref="messageListRef" :class="messageListClass" data-testid="chat-message-list">
        <div
          v-if="selectedMessages.length === 0"
          class="conference-text-helper text-conference-dock-text px-1 pt-1"
          data-testid="chat-empty-state"
        >
          No messages yet.
        </div>

        <article
          v-for="message in selectedMessages"
          :key="message.id"
          :class="[
            'max-w-full rounded-[1px] border px-2 py-1.5 shadow-none',
            message.isOwn
              ? 'self-end border-sky-200 bg-sky-50 text-sky-950'
              : message.toParticipantId
                ? 'self-start border-violet-200 bg-violet-50 text-violet-950'
                : message.isFunneled
                  ? 'self-start border-rose-200 bg-rose-50 text-rose-950'
                  : 'self-start border-slate-200 bg-white text-slate-900',
          ]"
          data-testid="chat-message"
        >
          <div
            class="conference-text-meta flex flex-wrap items-center justify-between gap-2 font-semibold tracking-[0.14em] uppercase opacity-80"
          >
            <span>
              {{
                message.toDisplayName
                  ? `${message.isOwn ? 'You' : message.fromDisplayName} to ${message.toDisplayName}`
                  : message.isOwn
                    ? 'You'
                    : message.fromDisplayName
              }}
            </span>
            <span>{{ timeFormatter.format(new Date(message.sentAt)) }}</span>
          </div>
          <p class="conference-text-chat-body mt-1 whitespace-pre-wrap" data-testid="chat-message-body">
            {{ message.body }}
          </p>
        </article>
      </div>

      <p
        class="conference-text-caption text-conference-dock-text px-2 pt-1"
        data-testid="chat-typing-indicator"
        style="min-height: 14px"
      >
        {{ typingLabel }}
      </p>

      <div
        :class="
          props.docked
            ? 'mt-3 flex items-center gap-2'
            : 'border-conference-dock-border mt-2 flex items-center gap-1 border-t px-2 py-2'
        "
      >
        <div class="flex-1">
          <CInput
            v-model="messageDraft"
            :size="props.docked ? 'md' : 'sm'"
            :tone="props.docked ? 'light' : 'chat'"
            data-testid="chat-composer"
            :disabled="isComposerDisabled"
            maxlength="300"
            placeholder="Write a message"
            @input="handleComposerInput"
            @keydown.enter.prevent="handleSendMessage"
          />
        </div>
        <div :class="props.docked ? '' : 'shrink-0'">
          <CButton
            :size="props.docked ? 'md' : 'sm'"
            tone="accent"
            data-testid="chat-send"
            :disabled="isComposerDisabled || isSendPending || !messageDraft?.trim?.()"
            @click="handleSendMessage"
          >
            Send
          </CButton>
        </div>
      </div>
    </template>
  </section>
</template>
